import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, Check, CalendarDays, Download, List, Loader2, Lock, Plus, RefreshCw, Rocket, RotateCcw, Trash2, Wand2 } from "lucide-react";

/** A run whose meetings were all created in Training Pipeline is locked. */
function isTpLocked(session: unknown): boolean {
  return (session as { tp_deploy_status?: string | null }).tp_deploy_status === "success";
}
import { toast } from "sonner";
import {
  getPeriodDetail,
  buildSchedule,
  saveDeviation,
  reviewDeviation,
  deleteDeviation,
  updateSession,
  addSession,
  removeSession,
  deployPeriod,
  getDeployProgress,
  refreshEnrollment,
  syncAllCalendarEvents,
} from "@/lib/schedule.functions";
import { confirmCoverage } from "@/lib/trainers.functions";
import { prepareTpSessionPayload } from "@/lib/training-pipeline.functions";
import type { TpSessionPayload } from "@/lib/tp-session-payload";
import {
  canTeachCourse,
  eligibleTrainers,
  NO_AUTHORIZED_TRAINER_MESSAGE,
} from "@/lib/authorization";
import { getMe } from "@/lib/core.functions";
import { PageHeader, EmptyState } from "@/components/AppShell";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";
import { sessionStage, STAGE_LABEL, STAGE_TONE } from "@/lib/session-stage";
import { formatDate, formatRange, formatTime, downloadCsv, toCsv } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { weeklyMeetingDates } from "@/lib/collab-days";

export const Route = createFileRoute("/_authenticated/schedule/$periodId")({
  head: () => ({
    meta: [
      { title: "Timeframe | Leadership Edge Scheduling" },
      { name: "description", content: "Collect deviation requests, preview the generated schedule, export to CSV, and deploy." },
      { property: "og:title", content: "Timeframe | Leadership Edge Scheduling" },
      { property: "og:description", content: "Preview, export, and deploy a course schedule." },
    ],
  }),
  component: PeriodPage,
});

const UNASSIGNED = "__none__";

interface TpPayloadReview {
  payload: TpSessionPayload;
  totals: {
    runs: number;
    sessions: number;
    included: number;
    skippedUnassigned: number;
    missingCourseId: number;
    missingClassId: number;
  };
}

type GroupKey = "none" | "course" | "trainer";
type SortKey = "date" | "course" | "trainer";
type ViewKey = "list" | "calendar";



interface SessionForm {
  id?: string;
  course_id: string;
  trainer_id: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
}

/** Each weekly meeting of a run, Session 1 through the final session. */
function sessionDates(session: { meeting_dates?: unknown; start_date: string; end_date: string }): string[] {
  const stored = session.meeting_dates;
  if (Array.isArray(stored) && stored.every((d) => typeof d === "string")) {
    return stored as string[];
  }
  return weeklyMeetingDates(session.start_date, session.end_date);
}

/** Weekday and date of one meeting, e.g. "Mon, Sep 21". */
function formatMeeting(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function PeriodPage() {
  const { periodId } = Route.useParams();
  const queryClient = useQueryClient();

  const fetchMe = useServerFn(getMe);
  const fetchDetail = useServerFn(getPeriodDetail);
  const build = useServerFn(buildSchedule);
  const addDeviation = useServerFn(saveDeviation);
  const review = useServerFn(reviewDeviation);
  const dropDeviation = useServerFn(deleteDeviation);
  const patchSession = useServerFn(updateSession);
  const createSession = useServerFn(addSession);
  const dropSession = useServerFn(removeSession);
  const deploy = useServerFn(deployPeriod);
  const fetchProgress = useServerFn(getDeployProgress);
  const syncCalendarFn = useServerFn(syncAllCalendarEvents);
  const [syncProgressId, setSyncProgressId] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<Awaited<ReturnType<typeof syncAllCalendarEvents>> | null>(null);
  const syncCalendarMutation = useMutation({
    mutationFn: (opts: { onlyFailed: boolean }) => {
      const id = crypto.randomUUID();
      setSyncProgressId(id);
      setSyncResult(null);
      return syncCalendarFn({ data: { periodId, progressId: id, onlyFailed: opts.onlyFailed } });
    },
    onSuccess: (r) => {
      setSyncProgressId(null);
      setSyncResult(r);
      queryClient.invalidateQueries({ queryKey: ["period", periodId] });
    },
    onError: (e: Error) => {
      setSyncProgressId(null);
      toast.error(e.message);
    },
  });
  const preparePayload = useServerFn(prepareTpSessionPayload);
  const syncEnrolled = useServerFn(refreshEnrollment);
  const coverConfirm = useServerFn(confirmCoverage);

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });
  const { data, isPending } = useQuery({
    queryKey: ["period", periodId],
    queryFn: () => fetchDetail({ data: { id: periodId } }),
  });

  const [sessionForm, setSessionForm] = useState<SessionForm | null>(null);
  const [sessionDeleteTarget, setSessionDeleteTarget] = useState<string | null>(null);
  const [buildNotesDraft, setBuildNotesDraft] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<GroupKey>("none");
  const [sortBy, setSortBy] = useState<SortKey>("date");
  const [view, setView] = useState<ViewKey>("list");
  const [deployConfirmOpen, setDeployConfirmOpen] = useState(false);
  const [actionConfirm, setActionConfirm] = useState<"retry" | "sync" | null>(null);
  const [payloadReview, setPayloadReview] = useState<TpPayloadReview | null>(null);
  const [deployResult, setDeployResult] = useState<Awaited<ReturnType<typeof deployPeriod>> | null>(null);

  const [deviationForm, setDeviationForm] = useState<{ trainer_id: string; start_date: string; end_date: string; reason: string } | null>(
    null,
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["period", periodId] });
    queryClient.invalidateQueries({ queryKey: ["periods"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const buildMutation = useMutation({
    mutationFn: (notes: string) => build({ data: { periodId, build_notes: notes.trim() || null } }),
    onSuccess: (result) => {
      toast.success(`${result.total} sessions built · ${result.unassigned} unassigned`);
      if (result.notesSummary) {
        if (result.notesSummary.startsWith("Notes recorded, but"))
          toast.warning(`${result.notesSummary} The schedule was built without them.`, { duration: 10000 });
        else toast.info(`Notes applied: ${result.notesSummary}`, { duration: 8000 });
      }
      setBuildNotesDraft(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const payloadMutation = useMutation({
    mutationFn: () => preparePayload({ data: { periodId } }),
    onSuccess: (result) => setPayloadReview(result),
    onError: (error: Error) => toast.error(error.message),
  });

  // UI-only staging of the single deploy call: TP step runs while the request
  // is pending; the calendar step is shown after TP returns, then results.
  const [deployStage, setDeployStage] = useState<"idle" | "tp" | "calendar">("idle");

  const [calendarRequested, setCalendarRequested] = useState(false);
  const [progressId, setProgressId] = useState<string | null>(null);
  const deployMutation = useMutation({
    mutationFn: (arg: boolean | { sync: boolean; onlyFailed: boolean }) => {
      const sync = typeof arg === "boolean" ? arg : arg.sync;
      const onlyFailed = typeof arg === "boolean" ? false : arg.onlyFailed;
      setDeployStage("tp");
      setCalendarRequested(sync);
      const id = crypto.randomUUID();
      setProgressId(id);
      return deploy({ data: { periodId, syncCalendar: sync, onlyFailed, progressId: id } });
    },
    onSuccess: async (result, sync) => {
      // Debug: full TP /virtual-session/create request and response per week.
      console.groupCollapsed(`[TP debug] /virtual-session/create: ${result.tpDebug?.length ?? 0} call(s)`);
      for (const call of result.tpDebug ?? []) {
        console.log(`Week ${call.week}: ${call.method} ${call.url} -> ${call.status ?? "no status"}`);
        console.log("Request", call.request);
        console.log("Response", call.response);
        if (call.error) console.error("Error", call.error);
      }
      console.groupEnd();
      const tpFailedAll = result.summary.submitted > 0 && result.summary.created === 0;
      void tpFailedAll;
      void sync;
      setDeployStage("idle");
      setProgressId(null);
      setDeployResult(result);
      const failed = result.results.filter((r) => r.status === "error").length;
      if (result.summary.failed > 0)
        toast.warning(`${result.summary.failed} of ${result.summary.submitted} session(s) failed in TP.`);
      else if (failed > 0) toast.warning(`Deployed with ${failed} target error(s). See the deploy log.`);
      else toast.success("Schedule deployed.");
      invalidate();
    },
    onError: (error: Error) => {
      setDeployStage("idle");
      setProgressId(null);
      toast.error(error.message);
    },
  });

  const liveProgress = useQuery({
    queryKey: ["deploy-progress", progressId],
    queryFn: () => fetchProgress({ data: { progressId: progressId! } }),
    enabled: Boolean(progressId) && deployStage !== "idle",
    refetchInterval: 1500,
  }).data;
  const syncLive = useQuery({
    queryKey: ["sync-progress", syncProgressId],
    queryFn: () => fetchProgress({ data: { progressId: syncProgressId! } }),
    enabled: Boolean(syncProgressId),
    refetchInterval: 1500,
  }).data;
  const deployBusy = payloadMutation.isPending || deployMutation.isPending || deployStage !== "idle";
  const deployProgress =
    deployStage === "calendar"
      ? { label: "Adding sessions to Google Calendar...", value: 75 }
      : { label: "Deploying sessions to Training Pipeline...", value: 25 };
  const calendarResult = deployResult?.results.find((r) => r.target === "calendar_invites");
  const calendarCounts = (() => {
    const d = calendarResult?.detail ?? "";
    const n = (k: string) => Number(d.match(new RegExp(`(\\d+) ${k}`))?.[1] ?? 0);
    return { ok: n("created") + n("updated"), failed: n("failed") };
  })();
  const tpResult = deployResult?.results.find((r) => r.target === "training_pipeline");

  const enrollMutation = useMutation({
    mutationFn: () => syncEnrolled({ data: { periodId } }),
    onSuccess: (result) => {
      toast.success(`Enrollment refreshed for ${result.updated} session(s).`);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const onError = (error: Error) => toast.error(error.message);
  const done = (message: string) => () => {
    toast.success(message);
    invalidate();
  };

  const deviationMutation = useMutation({
    mutationFn: (payload: { trainer_id: string; start_date: string; end_date: string; reason: string }) =>
      addDeviation({ data: { period_id: periodId, ...payload } }),
    onSuccess: done("Deviation request added."),
    onError,
  });
  const reviewMutation = useMutation({
    mutationFn: (payload: { id: string; status: "approved" | "denied" | "pending" }) => review({ data: payload }),
    onSuccess: done("Request updated."),
    onError,
  });
  const deviationDeleteMutation = useMutation({
    mutationFn: (id: string) => dropDeviation({ data: { id } }),
    onSuccess: done("Request removed."),
    onError,
  });
  const sessionDeleteMutation = useMutation({
    mutationFn: (id: string) => dropSession({ data: { id } }),
    onSuccess: done("Session removed."),
    onError,
  });
  const coverageMutation = useMutation({
    mutationFn: (payload: { sessionId: string; trainerId: string }) => coverConfirm({ data: payload }),
    onSuccess: done("Coverage confirmed."),
    onError,
  });

  const sessionSaveMutation = useMutation({
    mutationFn: (payload: SessionForm) => {
      const trainerId = payload.trainer_id === UNASSIGNED ? null : payload.trainer_id;
      return payload.id
        ? patchSession({
            data: {
              id: payload.id,
              trainer_id: trainerId,
              start_date: payload.start_date,
              end_date: payload.end_date,
              start_time: payload.start_time,
              end_time: payload.end_time,
            },
          })
        : createSession({
            data: {
              period_id: periodId,
              course_id: payload.course_id,
              trainer_id: trainerId,
              start_date: payload.start_date,
              end_date: payload.end_date,
              start_time: payload.start_time,
              end_time: payload.end_time,
            },
          });
    },
    onSuccess: () => {
      toast.success("Session saved.");
      setSessionForm(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data) return <EmptyState title="Not found" body="That timeframe no longer exists." />;

  const { period, sessions, deviations, trainers, courses, logs, changeLogs, exclusions, virtualClasses } = data;
  /** Synced TP Virtual Classes per course, in session order (display only). */
  const virtualClassesByCourse = new Map<string, { name: string; lms_class_id: number }[]>();
  for (const vc of virtualClasses ?? []) {
    const list = virtualClassesByCourse.get(vc.course_id) ?? [];
    list.push({ name: vc.name, lms_class_id: vc.lms_class_id });
    virtualClassesByCourse.set(vc.course_id, list);
  }
  /** Dev Pros allowed to teach a given course: all Dev Pros minus their exclusions. */
  const authorizedFor = (courseId: string) => eligibleTrainers(trainers, courseId, exclusions);

  const courseName = (s: (typeof sessions)[number]) => s.courses?.name ?? "Course";
  const trainerName = (s: (typeof sessions)[number]) => s.trainers?.full_name ?? "Unassigned";

  const sortedSessions = [...sessions].sort((a, b) => {
    if (sortBy === "course") return courseName(a).localeCompare(courseName(b)) || a.start_date.localeCompare(b.start_date);
    if (sortBy === "trainer") return trainerName(a).localeCompare(trainerName(b)) || a.start_date.localeCompare(b.start_date);
    return a.start_date.localeCompare(b.start_date) || a.start_time.localeCompare(b.start_time);
  });

  const groupedSessions: { key: string; label: string; items: typeof sortedSessions }[] = (() => {
    if (groupBy === "none") return [{ key: "all", label: "All sessions", items: sortedSessions }];
    const map = new Map<string, { key: string; label: string; items: typeof sortedSessions }>();
    for (const s of sortedSessions) {
      const key = groupBy === "course" ? courseName(s) : trainerName(s);
      const label = key;
      const existing = map.get(key);
      if (existing) existing.items.push(s);
      else map.set(key, { key, label, items: [s] });
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  })();

  const isAdmin = me?.isAdmin ?? false;
  const deployed = period.status === "deployed";
  const unassigned = sessions.filter((s) => s.status === "unassigned").length;
  const failedTpCount = sessions.filter((s) => (s as { tp_deploy_status?: string | null }).tp_deploy_status === "failed").length;
  const tpDeployedAll =
    failedTpCount === 0 &&
    (deployed ||
      (sessions.length > 0 && sessions.every((s) => (s as { tp_deploy_status?: string | null }).tp_deploy_status === "success")));
  const missingTrainer = Array.from(
    new Set(sessions.filter((s) => !s.trainer_id).map((s) => s.courses?.name ?? "Course")),
  ).sort();
  const coverage = sessions.filter((s) => s.coverage_requested);

  function exportCsv() {
    const rows: (string | number | null)[][] = [
      [
        "Course",
        "Code",
        "Session",
        "Session date",
        "Start time",
        "End time",
        "Trainer",
        "Trainer email",
        "Stage",
        "Enrolled",
      ],
      // One row per weekly meeting so every session of a run is listed.
      ...sessions.flatMap((s) => {
        const dates = s.trainer_id && s.start_date && s.end_date ? weeklyMeetingDates(s.start_date, s.end_date) : [""];
        return dates.map((date, i) => [
          s.courses?.name ?? "",
          s.courses?.code ?? "",
          i + 1,
          date,
          s.trainer_id ? s.start_time : "",
          s.trainer_id ? s.end_time : "",
          s.trainers?.full_name ?? "",
          s.trainers?.email ?? "",
          STAGE_LABEL[sessionStage(s.status)],
          s.enrolled_count ?? 0,
        ]);
      }),
    ];
    downloadCsv(`${period.name.replace(/\s+/g, "-").toLowerCase()}-schedule.csv`, toCsv(rows));
  }

  return (
    <>
      <Link to="/schedule" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> All timeframes
      </Link>

      <PageHeader
        eyebrow={`${period.status} · ${formatRange(period.start_date, period.end_date)}`}
        title={period.name}
        description={
          deployed
            ? "This schedule is deployed. Sessions are live on the website, in Training Pipeline, and on trainer calendars."
            : "Collect deviations, build the preview, adjust anything the engine could not place, then deploy."
        }
        actions={
          isAdmin ? (
            <>
              <Button
                variant="soft"
                onClick={() => setBuildNotesDraft(period.build_notes ?? "")}
                disabled={buildMutation.isPending || deployed}
              >
                <Wand2 /> {sessions.length > 0 ? "Rebuild preview" : "Build preview"}
              </Button>
              <Button variant="soft" onClick={exportCsv} disabled={sessions.length === 0}>
                <Download /> Export CSV
              </Button>
              {failedTpCount > 0 ? (
                <Button
                  variant="brass"
                  onClick={() => setActionConfirm("retry")}
                  disabled={deployBusy}
                  title="Sends only the sessions Training Pipeline rejected. Successful sessions are not sent again."
                >
                  {deployBusy ? <Loader2 className="animate-spin" /> : <RotateCcw />}{" "}
                  {deployBusy ? deployProgress.label : `Retry failed sessions (${failedTpCount})`}
                </Button>
              ) : (
                <Button
                  variant="brass"
                  onClick={() => setDeployConfirmOpen(true)}
                  disabled={deployBusy || sessions.length === 0 || tpDeployedAll}
                >
                  {deployBusy ? <Loader2 className="animate-spin" /> : <Rocket />}{" "}
                  {tpDeployedAll ? "Deployed to TP" : deployBusy ? deployProgress.label : "Deploy to Training Pipeline"}
                </Button>
              )}
              {deployed ? (
                (() => {
                  const eligible = sessions.filter((s) => isTpLocked(s));
                  const syncedIds = data?.calendarSyncedSessionIds ?? [];
                  const calendarAllSynced = eligible.length > 0 && eligible.every((s) => syncedIds.includes(s.id));
                  return (
                    <Button
                      variant={calendarAllSynced ? "brass" : "soft"}
                      onClick={() => setActionConfirm("sync")}
                      disabled={syncCalendarMutation.isPending || calendarAllSynced}
                      title={calendarAllSynced ? "Every deployed session is synced to Google Calendar." : undefined}
                    >
                      {syncCalendarMutation.isPending ? <Loader2 className="animate-spin" /> : <CalendarDays />}{" "}
                      {calendarAllSynced ? "Synced to Google Calendar" : "Deploy to Google Calendar"}
                    </Button>
                  );
                })()
              ) : null}
            </>
          ) : null
        }
      />

      <Dialog open={actionConfirm !== null} onOpenChange={(o) => { if (!o) setActionConfirm(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{actionConfirm === "retry" ? "Retry failed sessions only" : "Deploy to Google Calendar"}</DialogTitle>
            <DialogDescription>
              {actionConfirm === "retry"
                ? "This will resend only the sessions Training Pipeline rejected. Sessions that already deployed successfully will not be sent again, and Google Calendar will not be changed."
                : "This will sync every session deployed to Training Pipeline to each trainer's Google Calendar. Missing events are created, changed events are updated with the latest details, and unchanged events are skipped. No duplicates are created, and Training Pipeline is not changed."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="soft" onClick={() => setActionConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              onClick={() => {
                const a = actionConfirm;
                setActionConfirm(null);
                if (a === "retry") deployMutation.mutate({ sync: false, onlyFailed: true });
                else if (a === "sync") syncCalendarMutation.mutate({ onlyFailed: false });
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={syncCalendarMutation.isPending} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Deploying to Google Calendar</DialogTitle>
            <DialogDescription>Please keep this page open until it finishes.</DialogDescription>
          </DialogHeader>
          {(() => {
            const c = syncLive?.calendar;
            const total = c?.total ?? 0;
            const pct = total > 0 ? Math.round(((c?.done ?? 0) / total) * 100) : 0;
            return (
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2"><Loader2 className="size-4 animate-spin" /> {total > 0 ? `Calendar events: ${c?.done ?? 0} of ${total} completed` : "Preparing sessions..."}</p>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
                {c ? <p className="text-xs text-muted-foreground">{c.created} created · {c.updated} updated · {c.unchanged ?? 0} unchanged · {c.failed + c.notConnected} failed</p> : null}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={syncResult !== null} onOpenChange={(o) => { if (!o) setSyncResult(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{syncResult && syncResult.failed + syncResult.notConnected.length === 0 ? "Google Calendar deploy finished" : "Google Calendar deploy finished with problems"}</DialogTitle>
            <DialogDescription>Training Pipeline status is not affected by these results.</DialogDescription>
          </DialogHeader>
          {syncResult ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[["Created", syncResult.created], ["Updated", syncResult.updated], ["Unchanged", syncResult.unchanged], ["Failed", syncResult.failed]].map(([k, v]) => (
                  <div key={k as string} className="rounded-md border p-2 text-center">
                    <p className="text-lg font-semibold">{v as number}</p>
                    <p className="text-xs text-muted-foreground">{k as string}</p>
                  </div>
                ))}
              </div>
              {syncResult.notConnected.length > 0 ? (
                <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">Google Calendar not connected: {syncResult.notConnected.join(", ")}</p>
              ) : null}
              {syncResult.failedSessions.length > 0 ? (
                <div>
                  <p className="mb-1 font-medium">Failed sessions</p>
                  <ul className="space-y-1 text-xs">
                    {syncResult.failedSessions.map((f, i) => (
                      <li key={i} className="rounded-md border p-2">
                        <span className="font-medium">{f.label}</span>{f.date ? ` · ${f.date}` : ""}{f.trainer ? ` · ${f.trainer}` : ""}
                        <p className="text-muted-foreground">{f.message.slice(0, 200)}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {syncResult.skipped.length > 0 ? (
                <p className="text-xs text-muted-foreground">Skipped {syncResult.skipped.length}: {syncResult.skipped.slice(0, 5).join(", ")}</p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="soft" onClick={() => setSyncResult(null)}>Close</Button>
            {syncResult && (syncResult.failed > 0 || syncResult.notConnected.length > 0) ? (
              <Button variant="brass" onClick={() => syncCalendarMutation.mutate({ onlyFailed: true })}>
                <RotateCcw /> Retry failed sessions
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deployConfirmOpen} onOpenChange={setDeployConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deploy to Training Pipeline</DialogTitle>
            <DialogDescription>
              Are you sure you want to deploy this schedule? This will create all scheduled sessions in TP.
            </DialogDescription>
          </DialogHeader>
          {missingTrainer.length > 0 ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <p className="font-medium">Please assign a trainer to all courses before deploying the schedule.</p>
              <ul className="mt-2 list-disc space-y-0.5 pl-5">
                {missingTrainer.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="rounded-md border p-3 text-xs text-muted-foreground">
            Only Training Pipeline will be updated. Google Calendar is not changed during deployment — use the Deploy to Google Calendar button afterwards.
          </p>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="soft" onClick={() => setDeployConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              onClick={() => {
                if (missingTrainer.length > 0) return;
                setDeployConfirmOpen(false);
                deployMutation.mutate(false);
              }}
              disabled={deployBusy || missingTrainer.length > 0}
            >
              {deployBusy ? <Loader2 className="animate-spin" /> : <Rocket />} Deploy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={deployBusy}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg [&>button]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Deploying schedule</DialogTitle>
            <DialogDescription>Live results for each batch appear below as they come back.</DialogDescription>
          </DialogHeader>
          <DeployLiveProgress progress={liveProgress ?? null} calendarRequested={calendarRequested} />
        </DialogContent>
      </Dialog>

      <Dialog open={deployResult !== null} onOpenChange={(open) => (open ? null : setDeployResult(null))}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {deployResult?.summary.failed
                ? "Deployment finished with failures"
                : deployResult?.summary.submitted
                  ? "Deployment successful"
                  : "Deployment result"}
            </DialogTitle>
            <DialogDescription>
              {deployResult?.summary.failed
                ? "Some sessions were not created in Training Pipeline. This schedule is not fully deployed."
                : deployResult?.summary.submitted
                  ? "All scheduled sessions were successfully deployed to Training Pipeline."
                  : "No class sessions were submitted to Training Pipeline."}
            </DialogDescription>
          </DialogHeader>
          {deployResult ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  ["Total sessions", deployResult.summary.submitted, ""],
                  ["TP successful", deployResult.summary.created, "text-emerald-600"],
                  ["TP failed", deployResult.summary.failed, "text-destructive"],
                ].map(([label, value, cls]) => (
                  <div key={String(label)} className="rounded-md border p-3">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className={`text-xl font-semibold ${cls}`}>{value}</p>
                  </div>
                ))}
              </div>

              {tpResult?.status === "error" && deployResult.summary.created === 0 ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                  <p className="font-medium text-destructive">Training Pipeline deployment failed</p>
                  <p className="mt-1 text-muted-foreground">{tpResult.detail}</p>
                </div>
              ) : null}

              {deployResult.sessionResults.some((s) => s.status === "failed") ? (
                <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                  <p className="text-sm font-medium text-destructive">Failed sessions</p>
                  <p className="text-xs text-muted-foreground">
                    These stay editable. Fix them, then deploy again.
                  </p>
                  {deployResult.sessionResults
                    .filter((s) => s.status === "failed")
                    .map((s) => (
                      <div key={`failed-${s.client_session_id}`} className="rounded-md border bg-card p-2 text-sm">
                        <p className="font-medium">
                          {s.course_name} · Session {s.meeting_number}
                        </p>
                        <p className="text-muted-foreground">
                          {formatMeeting(s.start_date)} · {s.start_time}
                        </p>
                        <p className="mt-1 text-destructive">Error: {s.message}</p>
                      </div>
                    ))}
                </div>
              ) : null}


              <div className="space-y-1 text-sm text-muted-foreground">
                {deployResult.results.map((r) => (
                  <p key={r.target}>
                    {r.target.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}: {r.status}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button onClick={() => setDeployResult(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>




      <div className="mb-6 grid gap-4 sm:grid-cols-4">
        {[
          ["Scheduled courses", sessions.length],
          ["Unassigned", unassigned],
          ["Coverage requests", coverage.length],
          ["Enrolled", sessions.reduce((sum, s) => sum + (s.enrolled_count ?? 0), 0)],
        ].map(([label, value]) => (
          <div key={String(label)} className="panel p-4">
            <p className="eyebrow">{label}</p>
            <p className="mt-1.5 font-display text-2xl text-foreground">{value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="sessions">
        <TabsList>
          <TabsTrigger value="sessions">Schedule</TabsTrigger>
          <TabsTrigger value="deviations">Deviations ({deviations.length})</TabsTrigger>
          <TabsTrigger value="deploy">Deploy log</TabsTrigger>
          {isAdmin ? <TabsTrigger value="changes">Changes after deploy ({changeLogs.length})</TabsTrigger> : null}
        </TabsList>


        <TabsContent value="sessions" className="mt-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Sessions are placed only inside a trainer's teaching block, skipping approved deviations.
            </p>
            {isAdmin && !deployed ? (
              <Button
                variant="soft"
                size="sm"
                onClick={() =>
                  setSessionForm({
                    course_id: courses[0]?.id ?? "",
                    trainer_id: UNASSIGNED,
                    start_date: period.start_date,
                    end_date: period.start_date,
                    start_time: "09:00",
                    end_time: "09:30",
                  })
                }
                disabled={courses.length === 0}
              >
                <Plus /> Add course session
              </Button>
            ) : null}
            {isAdmin && deployed ? (
              <Button variant="soft" size="sm" onClick={() => enrollMutation.mutate()} disabled={enrollMutation.isPending}>
                <RefreshCw /> Refresh enrollment
              </Button>
            ) : null}
          </div>

          {sessions.length === 0 ? (
            <EmptyState title="Nothing built yet" body="Build the preview to generate sessions from each course's cadence." />
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">View</Label>
                  <div className="flex rounded-lg border border-border p-0.5">
                    <Button
                      variant={view === "list" ? "soft" : "ghost"}
                      size="sm"
                      className="h-7 px-3 text-xs"
                      onClick={() => setView("list")}
                    >
                      <List /> List
                    </Button>
                    <Button
                      variant={view === "calendar" ? "soft" : "ghost"}
                      size="sm"
                      className="h-7 px-3 text-xs"
                      onClick={() => setView("calendar")}
                    >
                      <CalendarDays /> Calendar
                    </Button>
                  </div>
                </div>
                {view === "list" ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">Group by</Label>
                      <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupKey)}>
                        <SelectTrigger className="h-8 w-[150px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No grouping</SelectItem>
                          <SelectItem value="course">Course</SelectItem>
                          <SelectItem value="trainer">Trainer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">Sort by</Label>
                      <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortKey)}>
                        <SelectTrigger className="h-8 w-[150px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="date">Start date</SelectItem>
                          <SelectItem value="course">Course name</SelectItem>
                          <SelectItem value="trainer">Trainer name</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : null}
              </div>

              {view === "calendar" ? (
                <ScheduleCalendar
                  sessions={sessions}
                  defaultMonth={period.start_date}
                  onSelectSession={
                    isAdmin
                      ? (sessionId) => {
                          const session = sessions.find((s) => s.id === sessionId);
                          if (!session) return;
                          if (isTpLocked(session)) {
                            toast.info("This session was deployed to Training Pipeline and is locked.");
                            return;
                          }
                          setSessionForm({
                            id: session.id,
                            course_id: session.course_id,
                            trainer_id: session.trainer_id ?? UNASSIGNED,
                            start_date: session.start_date,
                            end_date: session.end_date,
                            start_time: session.start_time.slice(0, 5),
                            end_time: session.end_time.slice(0, 5),
                          });
                        }
                      : undefined
                  }
                />
              ) : null}

              {view === "list" ? groupedSessions.map((group) => (
                <div key={group.key} className="mb-5 last:mb-0">
                  {groupBy !== "none" ? (
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h3 className="font-display text-base text-foreground">{group.label}</h3>
                      <span className="font-mono text-xs text-muted-foreground">{group.items.length} session(s)</span>
                    </div>
                  ) : null}
                  <div className="panel divide-y divide-border">
                    {group.items.map((session) => (

                <div key={session.id} className="flex flex-wrap items-start justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{session.courses?.name ?? "Course"}</p>
                    {session.trainer_id ? (
                      <>
                        <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                          {formatRange(session.start_date, session.end_date)} · {formatTime(session.start_time)}–
                          {formatTime(session.end_time)}
                        </p>
                        {(() => {
                          const dates = sessionDates(session);
                          const vcs = virtualClassesByCourse.get(session.course_id) ?? [];
                          const missing = dates.length - vcs.length;
                          return (
                            <>
                              {vcs.length === 0 ? (
                                <p className="mt-2 rounded-md border border-warning/40 bg-warning/15 px-2 py-1 text-xs font-medium text-warning-foreground">
                                  Virtual Class Learning Events have not been synced for this course.
                                </p>
                              ) : missing > 0 ? (
                                <p className="mt-2 rounded-md border border-warning/40 bg-warning/15 px-2 py-1 text-xs font-medium text-warning-foreground">
                                  {missing} Virtual Class Learning Event{missing === 1 ? " is" : "s are"} missing ({vcs.length} synced for {dates.length} session{dates.length === 1 ? "" : "s"}).
                                </p>
                              ) : null}
                              <ol className="mt-2 space-y-1.5">
                                {dates.map((date, index) => {
                                  const vc = vcs[index];
                                  return (
                                    <li key={date} className="font-mono text-xs text-muted-foreground">
                                      <span className="block font-sans font-medium text-foreground">
                                        {vc ? vc.name : `Session ${index + 1}`}
                                      </span>
                                      <span className="block">
                                        {formatMeeting(date)} · {formatTime(session.start_time)}–{formatTime(session.end_time)}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ol>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <p className="mt-1 text-xs font-medium text-destructive">No dates scheduled</p>
                    )}
                    {session.conflict_note ? (
                      <p className="mt-1.5 text-xs text-destructive">{session.conflict_note}</p>
                    ) : null}
                    {session.coverage_requested ? (
                      <p className="mt-1.5 text-xs text-warning-foreground">
                        Coverage requested{session.coverage_note ? `: ${session.coverage_note}` : ""}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {isAdmin && isTpLocked(session) ? null : (
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${STAGE_TONE[sessionStage(session.status)]}`}
                      >
                        {STAGE_LABEL[sessionStage(session.status)]}
                      </span>
                    )}
                    {typeof session.enrolled_count === "number" && session.enrolled_count > 0 ? (
                      <span className="rounded-full bg-success/12 px-2.5 py-1 text-xs font-medium text-success">
                        {session.enrolled_count} enrolled
                      </span>
                    ) : null}
                    <span
                      className={
                        session.trainer_id
                          ? "rounded-full bg-accent/12 px-2.5 py-1 text-xs font-medium text-accent"
                          : "rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive"
                      }
                    >
                      {session.trainers?.full_name ?? "Unassigned"}
                    </span>
                    {isAdmin ? (
                      <>
                        {session.coverage_requested ? (
                          <Select
                            onValueChange={(value) =>
                              coverageMutation.mutate({ sessionId: session.id, trainerId: value })
                            }
                          >
                            <SelectTrigger className="h-8 w-[170px] text-xs">
                              <SelectValue placeholder="Confirm coverage" />
                            </SelectTrigger>
                            <SelectContent>
                              {trainers.filter(
                                (t) =>
                                  t.is_active &&
                                  t.id !== session.trainer_id &&
                                  canTeachCourse(t, session.course_id, exclusions),
                              ).length === 0 ? (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                  {NO_AUTHORIZED_TRAINER_MESSAGE}
                                </div>
                              ) : (
                                trainers
                                  .filter(
                                    (t) =>
                                      t.is_active &&
                                      t.id !== session.trainer_id &&
                                      canTeachCourse(t, session.course_id, exclusions),
                                  )
                                  .map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                      {t.full_name}
                                    </SelectItem>
                                  ))
                              )}
                            </SelectContent>
                          </Select>
                        ) : null}
                        {isTpLocked(session) ? (
                          <>
                          <span
                            title="Deployed to Training Pipeline. Adjust and Delete are locked."
                            className="inline-flex items-center gap-1 rounded-full bg-success/12 px-2.5 py-1 text-xs font-medium text-success"
                          >
                            <Lock className="size-3" />
                            Deployed to TP
                          </span>
                          {data?.calendarSyncedSessionIds?.includes(session.id) ? (
                            <span
                              title="Google Calendar event created and saved for every deployed meeting."
                              className="ml-2 inline-flex items-center gap-1 rounded-full bg-success/12 px-2.5 py-1 text-xs font-medium text-success"
                            >
                              <Lock className="size-3" />
                              Synced to Google Calendar
                            </span>
                          ) : null}
                          </>
                        ) : (
                          <>
                            <Button
                              variant="soft"
                              size="sm"
                              onClick={() =>
                                setSessionForm({
                                  id: session.id,
                                  course_id: session.course_id,
                                  trainer_id: session.trainer_id ?? UNASSIGNED,
                                  start_date: session.start_date,
                                  end_date: session.end_date,
                                  start_time: session.start_time.slice(0, 5),
                                  end_time: session.end_time.slice(0, 5),
                                })
                              }
                            >
                              Adjust
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => setSessionDeleteTarget(session.id)}
                            >
                              <Trash2 />
                            </Button>
                          </>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
                    ))}
                  </div>
                </div>
              )) : null}
            </>
          )}

        </TabsContent>

        <TabsContent value="deviations" className="mt-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Approved requests block a trainer from being scheduled during those dates.
            </p>
            <Button
              variant="soft"
              size="sm"
              onClick={() =>
                setDeviationForm({
                  trainer_id: me?.trainerId ?? trainers[0]?.id ?? "",
                  start_date: period.start_date,
                  end_date: period.start_date,
                  reason: "",
                })
              }
              disabled={trainers.length === 0}
            >
              <Plus /> New request
            </Button>
          </div>

          {deviations.length === 0 ? (
            <EmptyState title="No deviation requests" body="Trainers can flag dates they are unavailable for this timeframe." />
          ) : (
            <div className="panel divide-y divide-border">
              {deviations.map((request) => (
                <div key={request.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className="font-medium text-foreground">{request.trainers?.full_name ?? "Trainer"}</p>
                    <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                      {formatRange(request.start_date, request.end_date)}
                    </p>
                    {request.reason ? <p className="mt-1.5 text-sm text-muted-foreground">{request.reason}</p> : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={
                        request.status === "approved"
                          ? "rounded-full bg-success/12 px-2.5 py-1 text-xs font-medium text-success"
                          : request.status === "denied"
                            ? "rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive"
                            : "rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
                      }
                    >
                      {request.status}
                    </span>
                    {isAdmin ? (
                      <>
                        <Button
                          variant="soft"
                          size="sm"
                          onClick={() => reviewMutation.mutate({ id: request.id, status: "approved" })}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => reviewMutation.mutate({ id: request.id, status: "denied" })}
                        >
                          Deny
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => deviationDeleteMutation.mutate(request.id)}
                        >
                          <Trash2 />
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deploy" className="mt-5">
          {logs.length === 0 ? (
            <EmptyState title="No deploys yet" body="Deploying pushes the catalog to the website, builds classes in Training Pipeline, and sends trainer invites." />
          ) : (
            <div className="panel divide-y divide-border">
              {logs.map((log) => (
                <div key={log.id} className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium capitalize text-foreground">{log.target.replace(/_/g, " ")}</p>
                    <span
                      className={
                        log.status === "success"
                          ? "rounded-full bg-success/12 px-2.5 py-1 text-xs font-medium text-success"
                          : log.status === "error"
                            ? "rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive"
                            : "rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
                      }
                    >
                      {log.status}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-muted-foreground">{log.detail}</p>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">{formatDate(log.created_at.slice(0, 10))}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {isAdmin ? (
          <TabsContent value="changes" className="mt-5">
            <p className="mb-4 text-sm text-muted-foreground">
              Every change made to a session after this schedule was deployed, newest first. Changed sessions are marked
              "Deployed - Edited" on the schedule.
            </p>
            {changeLogs.length === 0 ? (
              <EmptyState
                title="No changes after deploy"
                body="Once this schedule is deployed, any session that is edited, added, or removed is recorded here."
              />
            ) : (
              <div className="panel divide-y divide-border">
                {changeLogs.map((entry) => (
                  <div key={entry.id} className="p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-medium text-foreground">{entry.course_name ?? "Session"}</p>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground">
                        {entry.action}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm text-muted-foreground">{entry.summary}</p>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {new Date(entry.created_at).toLocaleString("en-US", { timeZone: "America/Phoenix" })}
                      {entry.changed_by_email ? ` · ${entry.changed_by_email}` : ""}
                      {entry.trainer_name ? ` · ${entry.trainer_name}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ) : null}
      </Tabs>


      <Dialog open={sessionForm !== null} onOpenChange={(open) => !open && setSessionForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{sessionForm?.id ? "Adjust session" : "Add course session"}</DialogTitle>
            <DialogDescription>Manual changes override the generated placement.</DialogDescription>
          </DialogHeader>

          {sessionForm ? (
            <div className="space-y-4">
              {!sessionForm.id ? (
                <div className="space-y-1.5">
                  <Label>Course</Label>
                  <Select
                    value={sessionForm.course_id}
                    onValueChange={(value) =>
                      setSessionForm({
                        ...sessionForm,
                        course_id: value,
                        // Changing the course recalculates who may teach it.
                        trainer_id: authorizedFor(value).some((t) => t.id === sessionForm.trainer_id)
                          ? sessionForm.trainer_id
                          : UNASSIGNED,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a course" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              <div className="space-y-1.5">
                <Label>Trainer</Label>
                <Select
                  value={sessionForm.trainer_id}
                  onValueChange={(value) => setSessionForm({ ...sessionForm, trainer_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                    {authorizedFor(sessionForm.course_id).map((trainer) => (
                      <SelectItem key={trainer.id} value={trainer.id}>
                        {trainer.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {sessionForm.course_id && authorizedFor(sessionForm.course_id).length === 0 ? (
                  <p className="text-xs text-destructive">{NO_AUTHORIZED_TRAINER_MESSAGE}</p>
                ) : null}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="session-start">Start date</Label>
                  <Input
                    id="session-start"
                    type="date"
                    value={sessionForm.start_date}
                    onChange={(e) => setSessionForm({ ...sessionForm, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="session-from">Starts</Label>
                  <Input
                    id="session-from"
                    type="time"
                    value={sessionForm.start_time}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (!/^\d{2}:\d{2}/.test(next)) {
                        setSessionForm({ ...sessionForm, start_time: next });
                        return;
                      }
                      // Keep the session length when the start moves, so the end always follows the start.
                      const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
                      const oldLen = toMin(sessionForm.end_time) - toMin(sessionForm.start_time);
                      const len = oldLen > 0 ? oldLen : 30;
                      const endMin = Math.min(toMin(next) + len, 23 * 60 + 59);
                      const end = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
                      setSessionForm({ ...sessionForm, start_time: next, end_time: end });
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter className="sm:justify-between">
            {sessionForm?.id && isAdmin ? (
              <Button
                variant="ghost"
                className="text-destructive hover:bg-destructive/10"
                disabled={sessionDeleteMutation.isPending}
                onClick={() => {
                  const id = sessionForm.id;
                  if (!id) return;
                  setSessionDeleteTarget(id);
                  setSessionForm(null);
                }}
              >
                <Trash2 />
                Delete session
              </Button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <Button variant="soft" onClick={() => setSessionForm(null)}>
                Cancel
              </Button>
              <Button
                variant="brass"
                disabled={sessionSaveMutation.isPending || !sessionForm?.course_id}
                onClick={() => sessionForm && sessionSaveMutation.mutate(sessionForm)}
              >
                Save session
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deviationForm !== null} onOpenChange={(open) => !open && setDeviationForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deviation request</DialogTitle>
            <DialogDescription>Dates a trainer should not be scheduled during this timeframe.</DialogDescription>
          </DialogHeader>

          {deviationForm ? (
            <div className="space-y-4">
              {isAdmin ? (
                <div className="space-y-1.5">
                  <Label>Trainer</Label>
                  <Select
                    value={deviationForm.trainer_id}
                    onValueChange={(value) => setDeviationForm({ ...deviationForm, trainer_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a trainer" />
                    </SelectTrigger>
                    <SelectContent>
                      {trainers.map((trainer) => (
                        <SelectItem key={trainer.id} value={trainer.id}>
                          {trainer.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="dev-start">From</Label>
                  <Input
                    id="dev-start"
                    type="date"
                    value={deviationForm.start_date}
                    onChange={(e) => setDeviationForm({ ...deviationForm, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="dev-end">To</Label>
                  <Input
                    id="dev-end"
                    type="date"
                    value={deviationForm.end_date}
                    onChange={(e) => setDeviationForm({ ...deviationForm, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="dev-reason">Reason</Label>
                <Textarea
                  id="dev-reason"
                  rows={3}
                  value={deviationForm.reason}
                  onChange={(e) => setDeviationForm({ ...deviationForm, reason: e.target.value })}
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="soft" onClick={() => setDeviationForm(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              disabled={deviationMutation.isPending}
              onClick={() => {
                if (!deviationForm) return;
                deviationMutation.mutate(deviationForm);
                setDeviationForm(null);
              }}
            >
              Submit request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sessionDeleteTarget !== null} onOpenChange={(open) => !open && setSessionDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete session?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this scheduled course and its meetings from this timeframe? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button variant="soft" onClick={() => setSessionDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={sessionDeleteMutation.isPending}
              onClick={() => {
                if (!sessionDeleteTarget) return;
                sessionDeleteMutation.mutate(sessionDeleteTarget);
                setSessionDeleteTarget(null);
              }}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={buildNotesDraft !== null} onOpenChange={(open) => !open && setBuildNotesDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{sessions.length > 0 ? "Rebuild preview" : "Build preview"}</DialogTitle>
            <DialogDescription>
              Notes to consider are read before the schedule is built. Leave them as they are, or adjust them for this build.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="build-notes">Notes to consider</Label>
            <Textarea
              id="build-notes"
              rows={5}
              value={buildNotesDraft ?? ""}
              onChange={(e) => setBuildNotesDraft(e.target.value)}
              placeholder="For example: no classes the week of Dec 22, keep everything between 9:00 and 15:00, skip Fridays, leave Derek out of this quarter."
            />
            <p className="text-xs text-muted-foreground">
              Rebuilding replaces the current preview and saves these notes on the timeframe.
            </p>
          </div>

          <DialogFooter>
            <Button variant="soft" onClick={() => setBuildNotesDraft(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              disabled={buildMutation.isPending}
              onClick={() => buildMutation.mutate(buildNotesDraft ?? "")}
            >
              {buildMutation.isPending ? "Building…" : sessions.length > 0 ? "Rebuild" : "Build"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>

  );
}

type LiveProgress = Awaited<ReturnType<typeof getDeployProgress>>;

const BATCH_LABEL: Record<string, string> = {
  waiting: "Waiting",
  in_progress: "In progress",
  success: "Success",
  partial: "Partial success",
  failed: "Failed",
};
const BATCH_TONE: Record<string, string> = {
  waiting: "text-muted-foreground",
  in_progress: "text-primary",
  success: "text-emerald-600",
  partial: "text-amber-600",
  failed: "text-destructive",
};

function DeployLiveProgress({ progress, calendarRequested }: { progress: LiveProgress; calendarRequested: boolean }) {
  const batches = progress?.tp.batches ?? [];
  const tpDone = batches.filter((b) => b.state !== "waiting" && b.state !== "in_progress");
  const tpSent = tpDone.reduce((n, b) => n + b.count, 0);
  const tpTotal = progress?.tp.total ?? 0;
  const cal = progress?.calendar;
  const calOn = calendarRequested && !cal?.skipped;
  // Progress bar from real completed work: TP sessions, then calendar events.
  const tpShare = calOn ? 0.6 : 1;
  const tpFrac = tpTotal > 0 ? tpSent / tpTotal : progress && progress.phase !== "starting" && progress.phase !== "tp" ? 1 : 0;
  const calFrac = cal && cal.total > 0 ? cal.done / cal.total : progress?.phase === "finishing" || progress?.phase === "done" ? 1 : 0;
  const value = Math.round((tpFrac * tpShare + (calOn ? calFrac * (1 - tpShare) : 0)) * 100);
  const current = batches.find((b) => b.state === "in_progress");
  const phaseLabel =
    !progress || progress.phase === "starting"
      ? "Preparing sessions..."
      : progress.phase === "tp"
        ? current
          ? `Training Pipeline: batch ${current.index} of ${batches.length}`
          : "Sending to Training Pipeline..."
        : progress.phase === "saving"
          ? "Saving Training Pipeline results..."
          : progress.phase === "calendar"
            ? `Google Calendar: ${cal?.done ?? 0} of ${cal?.total ?? 0} events`
            : "Finishing up...";

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <p className="text-sm font-medium">{phaseLabel}</p>
        <Progress value={value} />
        <p className="text-right text-xs text-muted-foreground">{value}%</p>
      </div>

      <section className="space-y-2 rounded-md border p-3">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold">Training Pipeline</h3>
          <span className="text-xs text-muted-foreground">
            {tpTotal} session(s) in {batches.length} weekly batch(es)
          </span>
        </div>
        {batches.length === 0 ? (
          <p className="text-xs text-muted-foreground">Waiting for batches...</p>
        ) : (
          <ul className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
            {batches.map((b) => (
              <li key={b.index} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span>
                    Batch {b.index} of {batches.length} <span className="text-muted-foreground">(week of {b.week})</span>: {b.count} session(s)
                  </span>
                  <span className={`flex items-center gap-1 font-medium ${BATCH_TONE[b.state]}`}>
                    {b.state === "in_progress" ? <Loader2 className="size-3 animate-spin" /> : null}
                    {BATCH_LABEL[b.state]}
                    {b.state !== "waiting" && b.state !== "in_progress" ? `: ${b.created} created, ${b.failed} failed` : ""}
                  </span>
                </div>
                {b.errors.length > 0 ? (
                  <ul className="mt-0.5 list-disc pl-5 text-destructive">
                    {b.errors.map((e, i) => (
                      <li key={i} className="break-words">{e}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {calendarRequested ? (
      <section className="space-y-2 rounded-md border p-3">
        <h3 className="text-sm font-semibold">Google Calendar</h3>
        {!cal || cal.total === 0 ? (
          <p className="text-xs text-muted-foreground">
            {progress?.phase === "finishing" || progress?.phase === "done"
              ? "No calendar events to send."
              : "Waiting for Training Pipeline to finish..."}
          </p>
        ) : (
          <div className="space-y-1 text-xs">
            <p>
              {cal.done} of {cal.total} event(s) processed, up to {cal.concurrency} at a time
              {cal.inFlight > 0 ? ` (${cal.inFlight} in progress)` : ""}
            </p>
            <p>
              <span className="text-emerald-600">{cal.created} created</span>,{" "}
              <span className="text-emerald-600">{cal.updated} updated</span>,{" "}
              <span className="text-destructive">{cal.failed} failed</span>
              {cal.notConnected > 0 ? <span className="text-muted-foreground">, {cal.notConnected} skipped (calendar not connected)</span> : null}
            </p>
            {cal.errors.length > 0 ? (
              <ul className="max-h-32 list-disc overflow-y-auto pl-5 text-destructive">
                {cal.errors.map((e, i) => (
                  <li key={i} className="break-words">{e}</li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </section>
      ) : null}
      <p className="text-sm text-muted-foreground">Please keep this page open while the schedule is being deployed...</p>
    </div>
  );
}
